/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import VehicleMovementDetailsApp
    from "../../../../../MaterialControl/Functionality/VehicleMovements/VehicleMovementDetailsApp";
import HandleChange from "../../../../../../../Helpers/HandleChange";
import {PHOTO_PICKER_MODEL} from "../../../../../../../components/PhotoPicker/SubComponent/Models/Models";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {errorMsg, isNotEmpty} from "../../../../../../../Apis/functions";
import {useDispatch, useSelector} from "react-redux";
import {
    CreateMaterialControlReport,
    RegisterGateEvent,
    UpdateMaterialControlMovementValues,
    UpdateMaterialControlTransport
} from "../../../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {
    MATERIAL_CONTROL_GATE_EVENT_MODEL,
    MATERIAL_CONTROL_GATE_EVENT_TYPE,
    MATERIAL_CONTROL_REPORT_MODEL,
    VEHICLE_MOVEMENT_DETAIL_MODEL
} from "../../../../../MaterialControl/Model/Model";
import {
    MATERIAL_CONTROL_GATE_MOVEMENT_RECORDED_STATUS,
    MATERIAL_CONTROL_REGISTERED_GATE_STATUS,
    showsGuardTransport,
    showsGuardTransportRecord
} from "../../../EntryAndExitForms/Details/Model/Model";
import {GATE_LOG_MODEL, GATE_TRANSPORT_FIELDS} from "../Model/Model";

/**
 * Detail of a movement of a vehicle read by the guard at the gate.
 *
 * The movement is read with the same layer the requester reads it with, so this
 * one only adds what the gate registers: the arrival of the vehicle while the
 * folio waits for it, and the dispatch that closes the movement.
 *
 * @returns {Object} Information of the movement and handlers of the gate log.
 */
const GuardVehicleMovementDetailsApp = () => {
    const dispatch = useDispatch();
    const {user} = useSelector((state) => state.authenticatioauthennSlice);
    // Every action of the gate is registered for the guard that performs it
    const userId = user?.data?.id;
    const details = VehicleMovementDetailsApp();
    const [gateLog, setGateLog] = useState({...GATE_LOG_MODEL});
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL});
    const [emptyFields, setEmptyFields] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const {handleChange} = HandleChange({model: gateLog, setModel: setGateLog});

    const statusId = details?.item?.dispatchStatusId;
    // The guard registers the return of the vehicle while the folio waits for it
    // — its arrival when the movement left the plant and its departure when it
    // came into it — and dispatches the movement once it is at the gate
    const isPendingArrival = [
        STATUS_MODEL.PENDING_ARRIVAL,
        STATUS_MODEL.PENDING_DEPARTURE
    ].includes(statusId);
    // The return of the vehicle is what the movement waits for, so the log the
    // guard wrote it with is the one the detail is left with
    const isRecorded = MATERIAL_CONTROL_GATE_MOVEMENT_RECORDED_STATUS.includes(statusId);
    // A movement reported with discrepancies is still open at the gate, so only
    // the dispatch and the return already registered leave it read
    const isClosed = MATERIAL_CONTROL_REGISTERED_GATE_STATUS.includes(statusId);
    const hasDiscrepancies = statusId === STATUS_MODEL.DISCREPANCY;
    const directionOption = details?.vehicleDirectionOption;
    // The vehicle is read in the moment it is in front of the guard, so a
    // movement that waits for the arrival of the unit reads it there and one
    // that goes straight to its dispatch reads it with that dispatch
    const showTransport = showsGuardTransport(details?.item);
    // The vehicle that was already written is read again on the step that
    // follows the one it was captured in, so the guard reads the unit the
    // movement travels with and no longer writes it
    const showTransportRecord = showsGuardTransportRecord(details?.item);

    /**
     * Opens the log with the vehicle that travels with the folio, so the guard
     * reads what was registered and completes the rest.
     *
     * Every movement the gate registers is written with a log of its own, so a
     * folio that still owes one opens it clean: what was captured for the
     * movement before does not travel with the next one, and the departure is
     * not written with what the dispatch was closed with. A movement already
     * written is only read, so the log opens with the notes and the evidence it
     * was registered with.
     */
    useEffect(() => {
        if (!details?.item) return;

        const lastGateEvent = (details.item.gateEvents || []).slice(-1)[0] || null;
        // The dispatch of the movement writes its own log, so the note it was
        // closed with is the one the dispatch card is read with
        const dispatchEvent = VEHICLE_MOVEMENT_DETAIL_MODEL.getMovementEvent(details.item);
        // The moment of the movement is the one the folio declares, and a folio
        // registered without it is read with the moment the gate stamped when
        // it dispatched the movement
        const movementDate = details.item.expectedMovementDate
            ?? dispatchEvent?.eventDate
            ?? null;
        // A folio that waits for a movement of the gate writes it from scratch
        const startsGateLog = !isClosed;
        // The return of the vehicle is written with the moment it happens, so
        // the date the folio declares is not read into the log of that movement
        const dateValue = isPendingArrival ? null : movementDate;

        setGateLog((prev) => ({
            ...prev,
            ...GATE_LOG_MODEL.set({
                item: details.item,
                dateName: directionOption?.dateName,
                dateValue,
                startsGateLog,
                lastGateEvent,
                dispatchEvent
            })
        }));

        if (startsGateLog) setPhotoPicker({...PHOTO_PICKER_MODEL});
    }, [details?.item?.id, details?.item?.dispatchStatusId, details?.item?.gateEvents?.length]);

    /**
     * Tells whether the movement can be closed with what the guard captured.
     *
     * The arrival is registered with the notes and the evidence of what came
     * back, and the dispatch with the vehicle that leaves and the moment it
     * happened.
     *
     * @returns {boolean} True when the gate log is complete.
     */
    const validFields = () => {
        // What the requester registered travels with the folio and is read in
        // the log, so a value is missing only when neither of them carries it
        const isCaptured = (name) => isNotEmpty(details?.item?.[name]) || isNotEmpty(gateLog?.[name]);

        // The unit is read from whoever is at the gate, so every value of it is
        // asked for by the log that displays the vehicle and not by the one
        // written once it is no longer captured
        const validTransport = !showTransport
            || GATE_TRANSPORT_FIELDS.every((name) => isCaptured(name));

        const valid = isPendingArrival
            ? validTransport && isNotEmpty(gateLog?.notes) && photoPicker?.listFiles?.length > 0
            : validTransport
                && (!directionOption || gateLog?.[directionOption.dateName] !== null)
                && isNotEmpty(gateLog?.dispatchNote);

        setEmptyFields(!valid);

        if (!valid) toast.warn(details?.t('please_fill_all_fields'));

        return valid;
    };

    /**
     * Registers the values the gate completed on the blocks of the movement.
     *
     * The vehicle and the driver live in the transport block, and the moment of
     * the movement is written on the folio itself, so it is only sent when the
     * requester left it blank: the folio keeps a single date and its history
     * says who wrote it.
     *
     * @returns {Promise<boolean>} Resolves with whether every block was
     * registered, true when there was nothing to send.
     */
    const registerGateValues = () => {
        const transport = gateLog?.toRegisterTransport(details?.item, userId);
        // The date and the notes are written on the folio itself in a single
        // update, and only when the requester left them blank
        const movementValues = gateLog?.toRegisterMovementValues(details?.item, directionOption?.dateName, userId);
        const calls = [];

        if (transport)
            calls.push(dispatch(UpdateMaterialControlTransport(transport)));

        if (movementValues)
            calls.push(dispatch(UpdateMaterialControlMovementValues(movementValues)));

        // The movement closes what the guard captured, so it is only written
        // once every value was registered; the first one turned down is the
        // notice the guard reads
        return Promise.all(calls).then((answers) => {
            const failed = answers
                .map(({payload}) => readAnswer(payload))
                .find((e) => !e.success);

            if (failed) {
                errorMsg(failed.code);
                // The values that were stored before one was turned down are
                // read again from the folio, so the retry sends only what is
                // missing
                details?.getInfo();
            }

            return !failed;
        });
    };

    /**
     * Reads the answer of a call of the material control service.
     *
     * A call answered with no body — the ones the service turns down — carries
     * no code to translate, so the answer is read as a failure and the status
     * of the response is the one the notice is written with.
     *
     * @param {Object} payload - Answer of the call.
     *
     * @returns {Object} Result of the call, read as success and code.
     */
    const readAnswer = (payload) => ({
        success: payload?.data?.success === true,
        code: payload?.data?.code ?? payload?.status ?? null
    });

    /**
     * Writes the movement of the gate the guard registered.
     *
     * The service stamps the moment the movement was registered on, so only
     * what the guard wrote about it travels with the log.
     *
     * @param {string} type - Movement the gate registers.
     * @param {string} [reason] - Comment written along with the movement.
     *
     * @returns {Promise} Resolves once the movement was written.
     */
    const registerGateMovement = (type, reason) =>
        dispatch(RegisterGateEvent(MATERIAL_CONTROL_GATE_EVENT_MODEL.toRegister({
            materialControlId: details?.item?.id,
            type: type,
            notes: reason || gateLog?.notes || gateLog?.dispatchNote,
            files: photoPicker?.listFiles,
            userId
        }))).then(({payload}) => {
            const {success, code} = readAnswer(payload);

            if (success) {
                setEmptyFields(false);
                toast.success(details?.t('has_been_updated_successfully'));
            } else errorMsg(code);

            details?.getInfo();
        });

    /**
     * Registers what the guard captured and leaves the movement in the status it
     * is closed with.
     *
     * Every movement of the gate, the dispatch included, is written with a log
     * of its own, and that log is the one that leaves the movement in its
     * status.
     *
     * @param {number} nextStatusId - Status the movement is left in.
     * @param {string} [reason] - Comment written along with the report.
     *
     * @returns {void}
     */
    const registerGateLog = (nextStatusId, reason) => {
        const type = MATERIAL_CONTROL_GATE_EVENT_TYPE[nextStatusId];

        registerGateValues().then((registered) => registered && registerGateMovement(type, reason));
    };

    /**
     * Registers the arrival of the vehicle, or the dispatch that closes the
     * movement once it is at the gate.
     *
     * @returns {void}
     */
    const handleDispatch = () => {
        if (!validFields()) return;

        if (!isPendingArrival) {
            registerGateLog(STATUS_MODEL.DISPATCHED);
            return;
        }

        // The return of the vehicle is registered before the dispatch, and the
        // status says which one of them the gate wrote
        registerGateLog(statusId === STATUS_MODEL.PENDING_DEPARTURE
            ? STATUS_MODEL.DEPARTURE_RECORDED
            : STATUS_MODEL.ARRIVAL_RECORDED);
    };

    /**
     * Opens the report of the discrepancies of the movement.
     *
     * @returns {void}
     */
    const openReportModal = () => setShowConfirmModal(true);

    /**
     * Closes the confirmation of the report.
     *
     * @returns {void}
     */
    const closeModal = () => setShowConfirmModal(false);

    /**
     * Registers the report of the discrepancies with the comment and the
     * evidence written on it.
     *
     * @param {string} comment - Comment of what was found at the gate.
     * @param {Array} [files] - Evidence of what was found, attached to the report.
     *
     * @returns {void}
     */
    const handleActionModal = (comment, files) => {
        // The report is an action of its own, so it only sends the discrepancy
        // and the values of the gate are registered with the movement
        dispatch(CreateMaterialControlReport(MATERIAL_CONTROL_REPORT_MODEL.toRegister({
            materialControlId: details?.item?.id,
            comment: comment,
            files: files,
            userId
        }))).then(({payload}) => {
            const {success, code} = readAnswer(payload);

            if (success) toast.success(details?.t('has_been_updated_successfully'));
            else errorMsg(code);

            details?.getInfo();
        });

        closeModal();
    };

    return {
        ...details,
        gateLog,
        setGateLog,
        handleChange,
        photoPicker,
        setPhotoPicker,
        emptyFields,
        isPendingArrival,
        isRecorded,
        isClosed,
        hasDiscrepancies,
        directionOption,
        showTransport,
        showTransportRecord,
        showConfirmModal,
        handleDispatch,
        openReportModal,
        handleActionModal,
        closeModal
    };
};

export default GuardVehicleMovementDetailsApp;
